import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import axios from 'axios'
import SuccessScreen from './SuccessScreen'
import FailureScreen from './FailureScreen'
import { useUIStore } from '../../store/uiStore'
import './PaymentPages.css'

export default function PaymentReturn() {
    const { setCurrentBoard } = useUIStore()
    const [status, setStatus] = useState<'loading' | 'success' | 'failed' | 'needs_code'>('loading')
    const [chargeInfo, setChargeInfo] = useState<{
        code?: string
        id?: string
        hostedUrl?: string
        usdtAmount?: number
        deliveryNetwork?: string
    } | null>(null)
    const [manualCode, setManualCode] = useState('')

    const searchParams = useMemo(() => new URLSearchParams(window.location.search), [])
    const pathname = useMemo(() => window.location.pathname, [])
    const orderIdFromPath = useMemo(() => {
        const m = pathname.match(/^\/order\/([^/]+)\/return\/?$/)
        return m?.[1]
    }, [pathname])

    const PENDING_KEY = 'flashusdt:pending_coinbase_checkout_v1'

    useEffect(() => {
        // Redirects are convenience; webhooks are reality. This page is UX-only.
        if (searchParams.get('cancelled') === 'true') {
            setStatus('failed')
            return
        }

        let initial: any = {}

        // Try to read identifier from Coinbase-return query params (different integrations use different keys).
        const qpCode =
            searchParams.get('charge_code') ||
            searchParams.get('chargeCode') ||
            searchParams.get('code')
        const qpId =
            searchParams.get('charge_id') ||
            searchParams.get('chargeId') ||
            searchParams.get('id')

        if (qpCode) initial.code = qpCode
        if (qpId) initial.id = qpId

        // Fall back to localStorage (most reliable for same-device mobile wallet handoffs).
        try {
            const raw = localStorage.getItem(PENDING_KEY)
            if (raw) {
                const parsed = JSON.parse(raw)
                // If old redirect format included /order/:orderId/return, prefer matching orderId when possible.
                if (!orderIdFromPath || parsed?.orderId === orderIdFromPath) {
                    initial = {
                        ...parsed,
                        code: initial.code || parsed?.chargeCode,
                        id: initial.id || parsed?.chargeId,
                        hostedUrl: parsed?.hostedUrl,
                        usdtAmount: parsed?.usdtAmount,
                        deliveryNetwork: parsed?.deliveryNetwork,
                    }
                }
            }
        } catch {
            // ignore
        }

        if (!initial.code && !initial.id) {
            setStatus('needs_code')
            setChargeInfo(null)
            return
        }

        setChargeInfo({
            code: initial.code,
            id: initial.id,
            hostedUrl: initial.hostedUrl,
            usdtAmount: initial.usdtAmount,
            deliveryNetwork: initial.deliveryNetwork,
        })

        let cancelled = false
        const poll = async () => {
            if (cancelled) return
            try {
                const params = initial.code
                    ? `code=${encodeURIComponent(initial.code)}`
                    : `id=${encodeURIComponent(initial.id)}`
                const response = await axios.get(`/api/payments/coinbase/charge-status?${params}`)
                const s = response.data?.status as string | undefined

                if (s === 'CONFIRMED') {
                    setStatus('success')
                    try { localStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
                    return
                }
                if (s === 'FAILED' || s === 'CANCELED' || s === 'EXPIRED') {
                    setStatus('failed')
                    try { localStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
                    return
                }

                // Still pending; poll again.
                setTimeout(poll, 3000)
            } catch (error) {
                console.error('Error checking Coinbase charge status:', error)
                // If network hiccups, keep polling instead of dead-ending.
                setTimeout(poll, 5000)
            }
        }

        poll()
        return () => {
            cancelled = true
        }
    }, [orderIdFromPath, searchParams])

    if (status === 'loading') {
        return (
            <div className="payment-page">
                <motion.div
                    className="payment-card glass"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                        <div className="payment-spinner" />
                    </div>
                    <h2 className="payment-title">Verifying payment…</h2>
                    <p className="payment-subtitle">Please wait while we confirm your transaction.</p>

                    {chargeInfo?.hostedUrl && (
                        <div className="payment-actions payment-actions--compact">
                            <button className="btn btn-secondary" onClick={() => window.location.href = '/payment/continue'}>
                                Continue payment
                            </button>
                        </div>
                    )}
                </motion.div>
            </div>
        )
    }

    if (status === 'needs_code') {
        return (
            <div className="payment-page">
                <motion.div
                    className="payment-card glass"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    <h2 className="payment-title">Find your payment</h2>
                    <p className="payment-subtitle">
                        If you completed payment but didn’t return here, paste your Coinbase Commerce
                        <strong> charge code</strong> (the part after <code>/charges/</code>) and we’ll
                        check it.
                    </p>

                    <div className="payment-inline">
                        <input
                            type="text"
                            value={manualCode}
                            onChange={(e) => setManualCode(e.target.value)}
                            placeholder="e.g. 66BEZC2Y"
                            className="payment-input"
                        />
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                const trimmed = manualCode.trim()
                                if (!trimmed) return
                                window.location.href = `/payment/return?code=${encodeURIComponent(trimmed)}`
                            }}
                        >
                            Check
                        </button>
                    </div>

                    <div className="payment-actions payment-actions--compact">
                        <button
                            className="btn btn-secondary"
                            onClick={() => window.open('mailto:support@flashusdt.com')}
                        >
                            Contact support
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={() => {
                                setCurrentBoard('ORDER')
                                window.history.replaceState({}, '', '/')
                            }}
                        >
                            Back to checkout
                        </button>
                    </div>
                </motion.div>
            </div>
        )
    }

    if (status === 'success') {
        return (
            <div className="payment-page" style={{ alignItems: 'flex-start' }}>
                <SuccessScreen
                    txHash={chargeInfo?.code || chargeInfo?.id || 'Coinbase Commerce'}
                    amount={chargeInfo?.usdtAmount}
                    network={chargeInfo?.deliveryNetwork || 'Coinbase Commerce'}
                    onReset={() => {
                        setCurrentBoard('ORDER')
                        window.history.replaceState({}, '', '/')
                    }}
                />
            </div>
        )
    }

    return (
        <div className="payment-page" style={{ alignItems: 'flex-start' }}>
            <FailureScreen
                error="Payment was not completed or was cancelled"
                onRetry={() => {
                    // Offer a safe recovery path: go back to order flow.
                    setCurrentBoard('ORDER')
                    window.history.replaceState({}, '', '/')
                }}
                onContactSupport={() => window.open('mailto:support@flashusdt.com')}
            />
        </div>
    )
}
